<template>
  <ListViewBanner :add-route="(user.roles.includes(Role.Admin) || user.roles.includes(Role.Capturer)) ? { name: 'app_plan_new', params: { district: route.params.district } } : null">
    Plans
  </ListViewBanner>

  <PlanSearch @search="searchParams = $event" />

  <AppContainer max-width="max-w-7xl">
    <AppTable>
      <AppTableHeader>
        <th>Name</th>
        <th>Status</th>
        <th>Last Updated</th>
        <th>Last Published</th>
        <th><span class="sr-only">Actions</span></th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!plans.length"
        :colspan="5"
      >
        <template v-if="plans.length">
          <tr
            v-for="plan in plans"
            :key="plan.id"
          >
            <td>
              <RouterLink
                class="text-secondary-alt"
                :to="{ name: `app_plan_overview`, params: { district: route.params.district, plan: plan.id } }"
              >
                {{ plan.name }}
              </RouterLink>

              <template v-if="plan.archived">
                <BadgeArchived />
              </template>
            </td>

            <td>
              <span :class="{ 'text-green-500': plan.status === PlanStatus.Published, 'text-blue-500': plan.status === PlanStatus.Draft }">
                {{ plan.status || '-' }}
              </span>
            </td>

            <td>
              <template v-if="plan.updatedAt">
                <span class="block text-gray-800">{{ dateFormatter.formatShortDateAndTime(plan.updatedAt) }}</span>
                <span
                  v-if="plan.updatedByUser"
                  class="block text-gray-400"
                >
                  by {{ plan.updatedByUser.firstName }} {{ plan.updatedByUser.lastName }}
                </span>
              </template>

              <template v-else>
                Never
              </template>
            </td>

            <td>
              <template v-if="plan.lastPublishedAt">
                <span class="block text-gray-800">{{ dateFormatter.formatShortDateAndTime(plan.lastPublishedAt) || 'Never' }}</span>
                <span class="block text-gray-400">by {{ plan.lastPublishedByUser.firstName }} {{ plan.lastPublishedByUser.lastName }}</span>
              </template>

              <template v-else>
                Never
              </template>
            </td>

            <td>
              <template v-if="plan.archived">
                <RestoreButton @click="planToRestore = plan" />
              </template>

              <template v-else>
                <AppDropdown>
                  <AppDropdownItem
                    text="Revisions"
                    :to="{ name: 'app_revision_list', params: { district: route.params.district, plan: plan.id } }"
                  />

                  <AppDropdownItem
                    text="Duplicate"
                    @click="planToDuplicate = plan"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="planToArchive = plan"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="planToArchive">
    <ConfirmationModal
      title="Archive Plan"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="planToArchive = false"
    >
      <p>
        <span class="block">Name: {{ planToArchive.name }}</span>
        Are you sure you want to <strong>ARCHIVE</strong> this Plan?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="planToRestore">
    <ConfirmationModal
      title="Restore Plan"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="planToRestore = false"
    >
      <p>
        <span class="block">Name: {{ planToRestore.name }}</span>
        Are you sure you want to <strong>RESTORE</strong> this Plan?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Duplicate Modal -->
  <template v-if="planToDuplicate">
    <ConfirmationModal
      title="Duplicate Plan"
      :processing="duplicating"
      @confirm="duplicateEntity"
      @close="planToDuplicate = false"
    >
      <p>
        <span class="block">Name: {{ planToDuplicate.name }}</span>
        Are you sure you want to DUPLICATE this Plan?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { PlanSearch } from '@/components/forms/search';
import { ConfirmationModal } from '@/components/modals';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

import { useDateFormatter, useHttp, useToasts } from '@/composables';
import { PlanStatus, Role } from '@/const';
import { RestoreButton } from '@/components/buttons';
import { useAuthStore } from '@/store';

const route = useRoute();
const authStore = useAuthStore();
const user = authStore.user;

const dateFormatter = useDateFormatter();
const toasts = useToasts();

const loading = ref();
const plans = ref([]);
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});
const searchParams = ref({
  page: 1,
});
const planToArchive = ref(null);
const planToDuplicate = ref(null);
const planToRestore = ref(null);
const restoring = ref(false);
const archiving = ref(false);
const duplicating = ref(false);

const loadPlans = async () => {
  loading.value = true;

  const response = await useHttp().get(`/districts/${route.params.district}/plans`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    plans.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const duplicateEntity = async () => {
  duplicating.value = true;

  const response = await useHttp().post(`/districts/${route.params.district}/plans/${planToDuplicate.value.id}/duplicate`);

  if (response.statusCode === HttpStatusCode.Ok) {
    plans.value = [];
    searchParams.value.page = 1;

    planToDuplicate.value = null;

    toasts.success('Plan duplicated');

    await loadPlans();
  }

  duplicating.value = false;
};

const archiveEntity = async () => {
  archiving.value = true;

  const response = await useHttp().post(`/districts/${route.params.district}/plans/${planToArchive.value.id}/archive`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success('Plan successfully archived');

    planToArchive.value = null;

    await loadPlans();
  } else {
    toasts.error('Could not archive plan');
  }

  planToArchive.value = null;
  archiving.value = false;
};

const restoreEntity = async () => {
  restoring.value = true;

  const response = await useHttp().post(`/districts/${route.params.district}/plans/${planToRestore.value.id}/restore`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success('Plan successfully restored');

    planToRestore.value = null;

    await loadPlans();
  } else {
    toasts.error('Could not restore plan');
  }

  restoring.value = false;
};

watchEffect(loadPlans);
</script>
